import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, syncAccountsInParallel } from '../../hooks/useApi';
import type { SyncAccountResult } from '../../types';
import { v2Path } from '../nav';
import { cx, FOCUS_RING } from './styles';
import {
  PreflightReport, PreflightRow, PreflightSyncOutcome, rowLabel, summariseSync,
} from './flashBuyPreflight';

/**
 * Shown by "Open DICE Drop" when some enabled accounts have not been synced
 * recently (see `flashBuyPreflight.ts`). Three ways out, all explicit:
 * sync the stale accounts right here, open the drop tool anyway, or go to
 * Connections. A seller who just synced never sees this.
 */

type Phase =
  | { kind: 'prompt' }
  | { kind: 'syncing'; done: number; total: number }
  | { kind: 'done'; outcome: PreflightSyncOutcome };

interface Props {
  report: PreflightReport;
  /** Mint the link and open the drop tool. Called from a click handler so
   *  popup blockers let the new tab through. */
  onOpen: () => void;
  onClose: () => void;
  onSyncComplete?: (outcome: PreflightSyncOutcome) => void;
  onOpenAnyway?: () => void;
}

const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={cx(className, 'animate-spin')} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const PRIMARY = cx(
  'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all',
  'bg-rose-500/10 border border-rose-500/20 text-rose-300 hover:bg-rose-500/20 hover:text-rose-200',
  'disabled:opacity-40 disabled:cursor-not-allowed',
  FOCUS_RING,
);
const SECONDARY = cx(
  'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all',
  'border border-white/[0.10] bg-white/[0.04] text-white/80 hover:border-white/20 hover:text-white',
  'disabled:opacity-40 disabled:cursor-not-allowed',
  FOCUS_RING,
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const AccountList: React.FC<{ rows: Array<{ row: PreflightRow; note?: string | null }> }> = ({ rows }) => (
  <ul className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-white/[0.08] bg-white/[0.02] text-xs">
    {rows.map(({ row, note }) => (
      <li key={row.id} className="flex items-baseline justify-between gap-3 px-3 py-1.5 border-b border-white/[0.05] last:border-b-0">
        <span className="truncate text-white/80">{rowLabel(row)}</span>
        {note && <span className="flex-shrink-0 text-white/40">{note}</span>}
      </li>
    ))}
  </ul>
);

export const FlashBuyPreflightModal: React.FC<Props> = ({ report, onOpen, onClose, onSyncComplete, onOpenAnyway }) => {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ kind: 'prompt' });
  const busy = phase.kind === 'syncing';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const handleSync = async () => {
    const rows = report.stale;
    setPhase({ kind: 'syncing', done: 0, total: rows.length });
    const results: SyncAccountResult[] = await syncAccountsInParallel(
      rows.map((r) => r.id),
      (done, total) => setPhase({ kind: 'syncing', done, total }),
    ).then((res) => res.results).catch(() => []);
    // The rows and the Connections page both show last-synced times; refresh
    // them so the next check sees the new timestamps.
    queryClient.invalidateQueries({ queryKey: queryKeys.dropConnections });
    queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    const outcome = summariseSync(rows, results);
    setPhase({ kind: 'done', outcome });
    onSyncComplete?.(outcome);
  };

  const handleOpenAnyway = () => { onOpenAnyway?.(); onOpen(); };

  const staleCount = report.stale.length;
  const reconnectCount = report.needsReconnect.length;
  const enabledCount = report.eligible.length + reconnectCount;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="flash-buy-preflight-title"
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-[#0f0f0f] sm:rounded-2xl"
      >
        <div className="border-b border-white/[0.08] px-5 py-4">
          <h2 id="flash-buy-preflight-title" className="text-base font-medium text-white">
            {phase.kind === 'done' ? 'Sync finished' : 'Sync your accounts first?'}
          </h2>
          <p className="mt-0.5 text-xs text-white/40">
            The drop tool uses the DICE login tokens FlashTools last saved. Tokens older than about an hour show up there as “Token Expired”.
          </p>
        </div>

        <div className="px-5 py-4 text-sm text-white/80 space-y-4">
          {phase.kind === 'prompt' && (
            <>
              {staleCount > 0 && (
                <div>
                  <p>
                    <span className="font-semibold text-white">{staleCount} of {plural(enabledCount, 'enabled account')}</span>
                    {' '}{staleCount === 1 ? 'has' : 'have'} not been synced in the last 45 minutes.
                    Syncing refreshes their tokens so they are ready when the drop tool opens.
                  </p>
                  <AccountList rows={report.stale.map((row) => ({ row, note: row.last_synced_at ? null : 'never synced' }))} />
                </div>
              )}
              {reconnectCount > 0 && (
                <div>
                  <p>
                    <span className="font-semibold text-amber-300">{plural(reconnectCount, 'account')}</span>
                    {' '}{reconnectCount === 1 ? 'needs' : 'need'} reconnecting. Syncing cannot fix {reconnectCount === 1 ? 'it' : 'them'}, and the drop tool will not list {reconnectCount === 1 ? 'it' : 'them'} at all until you reconnect in Connections.
                  </p>
                  <AccountList rows={report.needsReconnect.map((row) => ({ row, note: 'needs reauth' }))} />
                </div>
              )}
            </>
          )}

          {phase.kind === 'syncing' && (
            <div className="flex items-center gap-3">
              <SpinnerIcon className="w-5 h-5 text-rose-300" />
              <div>
                <p className="font-medium text-white">Syncing {phase.done}/{phase.total}…</p>
                <p className="text-xs text-white/40">Keep this tab open. Each account takes a few seconds.</p>
              </div>
            </div>
          )}

          {phase.kind === 'done' && (
            <div className="space-y-3">
              <p>
                <span className="font-semibold text-white">{phase.outcome.succeeded.length} synced</span>
                {', '}
                <span className={cx('font-semibold', phase.outcome.failed.length ? 'text-amber-300' : 'text-white')}>
                  {phase.outcome.failed.length} failed
                </span>
                .
              </p>
              {phase.outcome.failed.length > 0 ? (
                <div>
                  <p className="text-xs text-white/60">
                    These accounts will show as “Token Expired” in the drop tool. Reconnect them in Connections, or open anyway with the rest.
                  </p>
                  <AccountList rows={phase.outcome.failed.map(({ row, error }) => ({ row, note: error }))} />
                </div>
              ) : (
                <p className="text-xs text-white/60">Every account is fresh. You are good to open the drop tool.</p>
              )}
              {reconnectCount > 0 && (
                <p className="text-xs text-white/60">
                  {plural(reconnectCount, 'account')} still {reconnectCount === 1 ? 'needs' : 'need'} reconnecting and will not appear in the drop tool.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-white/[0.08] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to={v2Path('/accounts')}
            onClick={busy ? (e) => e.preventDefault() : undefined}
            aria-disabled={busy || undefined}
            className={cx('text-xs font-semibold text-white/50 hover:text-white/80 px-1 py-2', busy && 'pointer-events-none opacity-40', FOCUS_RING)}
          >
            {phase.kind === 'done' && phase.outcome.failed.length > 0 ? 'Fix in Connections' : 'Go to Connections'}
          </Link>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            {phase.kind === 'prompt' && (
              <>
                <button type="button" onClick={handleOpenAnyway} className={SECONDARY}>Open anyway</button>
                {staleCount > 0 ? (
                  <button type="button" onClick={handleSync} className={PRIMARY}>
                    Sync {plural(staleCount, 'account')} now
                  </button>
                ) : (
                  <button type="button" onClick={onClose} className={PRIMARY}>Close</button>
                )}
              </>
            )}
            {phase.kind === 'syncing' && (
              <button type="button" disabled className={PRIMARY}><SpinnerIcon /> Syncing…</button>
            )}
            {phase.kind === 'done' && (
              <>
                <button type="button" onClick={onClose} className={SECONDARY}>Close</button>
                <button type="button" onClick={onOpen} className={PRIMARY}>
                  {phase.outcome.failed.length > 0 ? 'Open anyway' : 'Open DICE Drop'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
